import React from 'react';
import useSWR from 'swr';
import { Tag, TagLabel, Text } from '@chakra-ui/react';
import { fetchData } from '../../services/fetch';

/**
 * EG-41 — CA05: exibe a classificação da questão como "Pilar > Tema".
 *
 * Resolve os nomes a partir dos ids aqui dentro, e não no card, por dois
 * motivos: o card não precisa saber que existe um catálogo, e o mesmo rótulo é
 * reaproveitado na listagem da classificação em massa. O SWR deduplica as duas
 * chamadas com os seletores que já rodam na mesma tela.
 *
 * Sobre o estado vazio: a primeira versão não renderizava nada, argumentando
 * que com 0 de 9.767 templates classificados um rótulo em toda linha seria
 * poluição. Revisto com o dev — é o contrário. Quem abre o template está
 * justamente varrendo o que falta classificar, e o silêncio esconde a
 * informação que ele foi procurar. Quem tiver um caso onde o aviso vira ruído
 * repetido passa `ocultarVazio`.
 */
const RotuloClassificacao = ({
  idPillar,
  idTheme,
  // GRI-08: os payloads passaram a mandar pilar e tema por NOME (o formulário
  // do respondente não pode resolver id -> nome, o catálogo é protegido).
  // Aceitar o nome pronto evita ter de espalhar id_pillar/id_theme por outros
  // 18 agregados só para esta tela, e faz os dois apps consumirem os MESMOS
  // campos — que era a origem do bug: o payload mandava nome e o card lia id.
  pillarName,
  themeTitle,
  ocultarVazio = false,
}) => {
  // Só busca o catálogo quando o nome NÃO veio pronto: uma requisição a menos
  // por tela no caminho novo.
  const precisaResolver = (!pillarName && idPillar) || (!themeTitle && idTheme);
  const { data: pilares } = useSWR(
    precisaResolver && idPillar ? '/pillar' : null,
    fetchData
  );
  const { data: temas } = useSWR(
    precisaResolver && idTheme ? '/theme' : null,
    fetchData
  );

  const pilar = (pilares || []).find(
    (item) => String(item.id_pillar) === String(idPillar)
  );
  const tema = (temas || []).find(
    (item) => String(item.id_theme) === String(idTheme)
  );

  // Classificação apontando para item removido do catálogo cai no mesmo caso do
  // não classificado: o que não resolve não vira texto quebrado na tela.
  const partes = [
    pillarName || pilar?.name_pillar,
    themeTitle || tema?.title,
  ].filter(Boolean);

  // Cores explícitas, sem colorScheme. O tema do projeto sobrescreve blue.100
  // com #0062FF — um azul FORTE de marca, usado em botões primários. O
  // variant='subtle' do Chakra usa o tom 100 como FUNDO esperando um tint
  // claro, e o resultado é texto escuro sobre azul saturado: ilegível. É a
  // mesma armadilha do red.100 que a EG-40 encontrou guardando um verde.
  if (partes.length === 0) {
    if (ocultarVazio) return null;

    return (
      <Tag size='sm' mt='4px' bg='gray.100' color='gray.600'>
        <TagLabel fontSize='12px'>Sem classificação</TagLabel>
      </Tag>
    );
  }

  return (
    <Tag size='sm' mt='4px' maxW='100%' bg='#E8F0FE' color='#0B4A8F'>
      <TagLabel fontSize='12px'>
        <Text as='span' noOfLines={1}>
          {partes.join(' > ')}
        </Text>
      </TagLabel>
    </Tag>
  );
};

export default RotuloClassificacao;
